// src/services/songService.ts - Updated to handle custom month
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';
import { isAuthenticated, getAccessToken } from './authService';

export interface SongDetails {
  id: number;
  title: string;
  releaseDate: string;
  duration: string;
  streams: number;
  previousStreams: number;
  score: number;
  genre: string;
  saveRate?: number;
  // Added new properties for expanded metrics
  listeners?: number;
  saves?: number;
  playlist_adds?: number;
  playlists?: {
    id: number;
    spotifyId: string;
    name: string;
    owner: string;
    followerCount: number;
    imageUrl: string | null;
  }[];
  // Added for historical data - used in charts
  metricsHistory?: {
    month: string;
    streams: number;
    listeners?: number;
    saves?: number;
    playlist_adds?: number;
  }[];
  // Audience demographics
  audienceDemographics?: {
    age: {
      '18-24': number;
      '25-34': number;
      '35-44': number;
      '45+': number;
    };
    gender: {
      male: number;
      female: number;
    };
  };
}

export const getSongDetails = async (songId: string): Promise<SongDetails> => {
  try {
    if (!isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    const token = getAccessToken();
    const config = token ? {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } : {};

    const response = await axios.get(
      getApiUrl(`/api/song/${songId}`),
      config
    );

    // Normalize the release date property with multiple fallbacks
    const data = response.data;
    data.releaseDate = 
      data.releaseDate || 
      data.release_date || 
      data.date || 
      new Date().toISOString();

    // Ensure it's a valid date string
    if (!data.releaseDate) {
      console.warn('No release date found, using current date');
      data.releaseDate = new Date().toISOString();
    }

    return data;
  } catch (error: unknown) {
    // Type-safe error handling
    if (axios.isAxiosError(error)) {
      console.error('Axios error fetching song details:', error.response?.data);
      
      // Fallback with data from axios error response
      return {
        id: parseInt(songId),
        title: error.response?.data?.title || 'Unknown Song',
        releaseDate: new Date().toISOString(),
        duration: '0:00',
        streams: 0,
        previousStreams: 0,
        score: 0,
        genre: error.response?.data?.genre || 'Unknown'
      };
    }

    // Handle other types of errors
    if (error instanceof Error) {
      console.error('Error fetching song details:', error.message);
    } else {
      console.error('Unknown error fetching song details', error);
    }

    // Generic fallback
    return {
      id: parseInt(songId),
      title: 'Unknown Song',
      releaseDate: new Date().toISOString(),
      duration: '0:00',
      streams: 0,
      previousStreams: 0,
      score: 0,
      genre: 'Unknown'
    };
  }
};

/**
 * Add new metrics for a song
 */
export const addSongMetrics = async (
  songId: string, 
  metricsData: {
    streams: number;
    listeners: number;
    saves: number;
    playlist_adds: number;
    notes?: string;
    metric_date?: string; // Optional parameter for custom month
  }
): Promise<{ success: boolean, data?: any }> => {
  try {
    if (!isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    const token = getAccessToken();
    const config = token ? {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } : {};

    // Ensure all values are numbers
    const formattedData = {
      streams: parseInt(String(metricsData.streams), 10) || 0,
      listeners: parseInt(String(metricsData.listeners), 10) || 0,
      saves: parseInt(String(metricsData.saves), 10) || 0,
      playlist_adds: parseInt(String(metricsData.playlist_adds), 10) || 0,
      notes: metricsData.notes || '',
      metric_date: metricsData.metric_date || undefined // Pass the custom month if provided
    };

    console.log('Sending song metrics:', formattedData);

    const response = await axios.post(
      getApiUrl(`/api/song/${songId}/metrics`),
      formattedData,
      config
    );

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error adding song metrics:', error);
    return { success: false };
  }
};

/**
 * Get metrics history for a song
 */
export const getSongMetricsHistory = async (
  songId: string
): Promise<{
  month: string;
  streams: number;
  listeners: number;
  saves: number;
  playlist_adds: number;
}[]> => {
  try {
    if (!isAuthenticated()) {
      console.warn('User not authenticated for getSongMetricsHistory');
      return [];
    }

    const token = getAccessToken();
    const config = token ? {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } : {};

    const response = await axios.get(
      getApiUrl(`/api/song/${songId}/metrics-history`),
      config
    );

    // Ensure we return an array with the correct structure
    if (response.data && Array.isArray(response.data)) {
      return response.data.map(item => ({
        month: item.month,
        streams: item.streams || 0,
        listeners: item.listeners || 0,
        saves: item.saves || 0,
        playlist_adds: item.playlist_adds || 0
      }));
    } else {
      console.warn('Unexpected response format from metrics history:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching song metrics history:', error);
    // Return an empty array on error
    return [];
  }
};